/**
 * Sections of the monthly report (Financiar → Rapoarte lunare), in the order
 * an entrepreneur asks the questions: how was the month, where is the money,
 * who owes me, whom do I owe, what is coming. Every section says out loud when
 * its data is missing instead of showing zeros.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type {
  AccountsSection as AccountsData,
  DataQualitySection as DataQualityData,
  ExpensesSection as ExpensesData,
  OpenInvoicesSection as OpenInvoicesData,
  PayablesSection as PayablesData,
  PartyAmount,
  ProjectsSection as ProjectsData,
  ReportSummary,
  RevenueSection as RevenueData,
  VatSection as VatData,
} from '../../../lib/monthlyReportApi';
import {
  AGING_BUCKET_ORDER,
  SUBSCRIPTION_STATUS_CLASS,
  fmtDate,
  fmtMoney,
  fmtPct,
  fmtSignedMoney,
} from '../../../lib/monthlyReportFormat';
import { useI18n } from '../../../contexts/I18nContext';

const OPEN_PREVIEW = 8;

// --- Shared bits ---------------------------------------------------------------

export function ReportSection({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {aside}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: React.ReactNode;
  tone?: 'neutral' | 'good' | 'bad';
}) {
  const valueClass =
    tone === 'good' ? 'text-green-700' : tone === 'bad' ? 'text-red-700' : 'text-gray-900';
  return (
    // Two per row on phones: smaller type there, and long figures may wrap
    // rather than push the card wider than its column.
    <div className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 sm:text-xs">
        {label}
      </p>
      <p
        className={`mt-1 text-base font-semibold tabular-nums [overflow-wrap:anywhere] sm:text-xl ${valueClass}`}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export function Empty({ text }: { text: string }) {
  return <p className="text-sm text-gray-500">{text}</p>;
}

export function ChangeHint({ change, fallback }: { change: number | null; fallback: string }) {
  const { t } = useI18n();
  if (change === null) return <>{fallback}</>;
  const up = change >= 0;
  return (
    <span className={up ? 'text-green-600' : 'text-red-600'}>
      {up ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%{' '}
      <span className="text-gray-400">{t('monthlyReport.summary.vsPrevious')}</span>
    </span>
  );
}

export function PartyList({ title, rows }: { title: string; rows: PartyAmount[] }) {
  const { locale } = useI18n();
  if (rows.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      <ul className="mt-2 divide-y divide-gray-100">
        {rows.map((r) => (
          <li key={r.name} className="flex items-center justify-between gap-3 py-1.5 text-sm">
            <span className="truncate text-gray-800">{r.name}</span>
            <span className="shrink-0 tabular-nums text-gray-900">
              {fmtMoney(r.amount, 'RON', locale)}
              <span className="ml-2 text-xs text-gray-400">{fmtPct(r.share)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- 1. Summary ---------------------------------------------------------------------

export function SummarySection({ summary }: { summary: ReportSummary }) {
  const { t, locale } = useI18n();
  const noPrev = t('monthlyReport.summary.noPrevious');
  return (
    <ReportSection title={t('monthlyReport.section.summary')}>
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <Stat
          label={t('monthlyReport.summary.inflows')}
          value={fmtMoney(summary.inflows, 'RON', locale)}
          hint={<ChangeHint change={summary.change.inflows} fallback={noPrev} />}
        />
        <Stat
          label={t('monthlyReport.summary.outflows')}
          value={fmtMoney(summary.outflows, 'RON', locale)}
          hint={<ChangeHint change={summary.change.outflows} fallback={noPrev} />}
        />
        <Stat
          label={t('monthlyReport.summary.net')}
          value={fmtMoney(summary.net, 'RON', locale)}
          tone={summary.net >= 0 ? 'good' : 'bad'}
          hint={<ChangeHint change={summary.change.net} fallback={noPrev} />}
        />
        <Stat
          label={t('monthlyReport.summary.bankBalance')}
          value={summary.bankBalance === null ? '—' : fmtMoney(summary.bankBalance, 'RON', locale)}
          hint={
            summary.bankBalance === null
              ? t('monthlyReport.summary.bankBalanceUnknown')
              : `${t('monthlyReport.summary.bankBalanceAsOf', { date: fmtDate(summary.bankBalanceAsOf, locale) })}${
                  summary.bankBalanceChange !== null
                    ? ` · ${fmtSignedMoney(summary.bankBalanceChange, 'RON', locale)}`
                    : ''
                }`
          }
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
        <span className="font-medium text-gray-900">
          {t('monthlyReport.summary.invoicedVsCollected')}:
        </span>
        <span>
          {t('monthlyReport.summary.invoiced')}{' '}
          <span className="font-semibold">{fmtMoney(summary.invoiced, 'RON', locale)}</span>
        </span>
        <span>
          {t('monthlyReport.summary.collected')}{' '}
          <span className="font-semibold">{fmtMoney(summary.collected, 'RON', locale)}</span>
        </span>
      </div>
      {summary.financing &&
        (summary.financing.byCategory.length > 0 ||
          (summary.financing.otherCurrencies?.inflows.length ?? 0) > 0 ||
          (summary.financing.otherCurrencies?.outflows.length ?? 0) > 0) && (
          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <p className="font-medium text-gray-700">
              Finanțare și distribuiri (nu intră în încasări și plăți de mai sus): intrări{' '}
              {fmtMoney(summary.financing.inflows, 'RON', locale)}, ieșiri{' '}
              {fmtMoney(summary.financing.outflows, 'RON', locale)}
            </p>
            <ul className="mt-1 space-y-0.5">
              {summary.financing.byCategory.map((c) => (
                <li key={c.code}>
                  {c.label}:{' '}
                  {[
                    c.inflows ? `+${fmtMoney(c.inflows, 'RON', locale)}` : null,
                    c.outflows ? `-${fmtMoney(c.outflows, 'RON', locale)}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </li>
              ))}
              {[
                ...(summary.financing.otherCurrencies?.inflows ?? []).map(
                  (c) => `+${fmtMoney(c.amount, c.currency, locale)}`,
                ),
                ...(summary.financing.otherCurrencies?.outflows ?? []).map(
                  (c) => `-${fmtMoney(c.amount, c.currency, locale)}`,
                ),
              ].length > 0 && (
                <li>
                  {t('monthlyReport.summary.otherCurrencies')}:{' '}
                  {[
                    ...(summary.financing.otherCurrencies?.inflows ?? []).map(
                      (c) => `+${fmtMoney(c.amount, c.currency, locale)}`,
                    ),
                    ...(summary.financing.otherCurrencies?.outflows ?? []).map(
                      (c) => `-${fmtMoney(c.amount, c.currency, locale)}`,
                    ),
                  ].join(' · ')}
                </li>
              )}
            </ul>
          </div>
        )}
      {(summary.otherCurrencies.inflows.length > 0 ||
        summary.otherCurrencies.outflows.length > 0) && (
        <p className="mt-2 text-xs text-gray-500">
          {t('monthlyReport.summary.otherCurrencies')}:{' '}
          {summary.otherCurrencies.inflows
            .map((c) => `+${fmtMoney(c.amount, c.currency, locale)}`)
            .concat(
              summary.otherCurrencies.outflows.map(
                (c) => `-${fmtMoney(c.amount, c.currency, locale)}`,
              ),
            )
            .join(' · ')}
        </p>
      )}
    </ReportSection>
  );
}

// --- 2. Accounts --------------------------------------------------------------------

export function AccountsSection({ accounts }: { accounts: AccountsData }) {
  const { t, locale } = useI18n();
  return (
    <ReportSection title={t('monthlyReport.section.accounts')}>
      {accounts.items.length === 0 ? (
        <Empty text={t('monthlyReport.accounts.none')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="pb-2 pr-3 font-medium">{t('monthlyReport.accounts.account')}</th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.accounts.opening')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.accounts.credits')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.accounts.debits')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.accounts.net')}
                </th>
                <th className="pb-2 text-right font-medium">
                  {t('monthlyReport.accounts.closing')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.items.map((a) => (
                <tr key={a.key} className="align-top">
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-900">
                      {a.bankName}
                      {a.label ? ` · ${a.label}` : ''}
                      {a.key.startsWith('iban:') && (
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {t('monthlyReport.accounts.unregistered')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {a.iban ?? '—'} · {a.currency}
                    </div>
                    {a.missingStatement ? (
                      <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {t('monthlyReport.accounts.missing')}
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-gray-500">
                        {t('monthlyReport.accounts.transactions', { n: a.transactions })}
                        {a.internalTransfers > 0 &&
                          ` · ${t('monthlyReport.accounts.internal', { n: a.internalTransfers })}`}
                        {a.unreconciled > 0 && (
                          <span className="text-amber-700">
                            {' · '}
                            {t('monthlyReport.accounts.unreconciled', { n: a.unreconciled })}
                          </span>
                        )}
                        {a.unfinalizedStatements > 0 && (
                          <span className="text-amber-700">
                            {' · '}
                            {t('monthlyReport.accounts.unfinalized', {
                              n: a.unfinalizedStatements,
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700">
                    {a.openingBalance === null
                      ? '—'
                      : fmtMoney(a.openingBalance, a.currency, locale)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-900">
                    {fmtMoney(a.credits, a.currency, locale)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-900">
                    {fmtMoney(a.debits, a.currency, locale)}
                  </td>
                  <td
                    className={`py-2 pr-3 text-right tabular-nums font-medium ${a.net >= 0 ? 'text-green-700' : 'text-red-700'}`}
                  >
                    {fmtSignedMoney(a.net, a.currency, locale)}
                  </td>
                  <td className="py-2 text-right tabular-nums font-medium text-gray-900">
                    {a.closingBalance === null
                      ? '—'
                      : fmtMoney(a.closingBalance, a.currency, locale)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportSection>
  );
}

// --- 3. Revenue ---------------------------------------------------------------------

export function RevenueSection({ revenue }: { revenue: RevenueData }) {
  const { t, locale } = useI18n();
  const inv = revenue.invoices;
  const empty = inv.count === 0 && inv.creditNotes === 0 && revenue.collected === 0;
  return (
    <ReportSection title={t('monthlyReport.section.revenue')}>
      {empty ? (
        <Empty text={t('monthlyReport.revenue.none')} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
            <Stat
              label={t('monthlyReport.revenue.invoices')}
              value={String(inv.count)}
              hint={
                inv.creditNotes > 0
                  ? t('monthlyReport.revenue.creditNotes', { n: inv.creditNotes })
                  : undefined
              }
            />
            <Stat
              label={t('monthlyReport.revenue.total')}
              value={fmtMoney(inv.total, 'RON', locale)}
              hint={`${t('monthlyReport.revenue.net')} ${fmtMoney(inv.net, 'RON', locale)} · ${t('monthlyReport.revenue.vat')} ${fmtMoney(inv.vat, 'RON', locale)}`}
            />
            <Stat
              label={t('monthlyReport.revenue.collected')}
              value={fmtMoney(revenue.collected, 'RON', locale)}
            />
            {revenue.concentration && (
              <Stat
                label={t('monthlyReport.revenue.topInvoiced')}
                value={fmtPct(revenue.concentration.share)}
                tone={revenue.concentration.share > 50 ? 'bad' : 'neutral'}
                hint={
                  <>
                    {t('monthlyReport.revenue.concentration', {
                      name: revenue.concentration.name,
                      share: revenue.concentration.share,
                    })}
                    {revenue.concentration.share > 50 && (
                      <span className="block text-red-600">
                        {t('monthlyReport.revenue.concentrationWarning')}
                      </span>
                    )}
                  </>
                }
              />
            )}
          </div>
          {revenue.otherCurrencies.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              {t('monthlyReport.open.otherCurrencies')}:{' '}
              {revenue.otherCurrencies
                .map((c) => fmtMoney(c.amount, c.currency, locale))
                .join(' · ')}
            </p>
          )}
          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
            <PartyList
              title={t('monthlyReport.revenue.topInvoiced')}
              rows={revenue.topClientsInvoiced}
            />
            <PartyList
              title={t('monthlyReport.revenue.topCollected')}
              rows={revenue.topClientsCollected}
            />
          </div>
        </>
      )}
    </ReportSection>
  );
}

// --- 4. Expenses --------------------------------------------------------------------

export function ExpensesSection({ expenses }: { expenses: ExpensesData }) {
  const { t, locale } = useI18n();
  return (
    <ReportSection
      title={t('monthlyReport.section.expenses')}
      aside={
        <span className="text-sm font-semibold text-gray-900">
          {fmtMoney(expenses.total, 'RON', locale)}
        </span>
      }
    >
      {expenses.categories.length === 0 ? (
        <Empty text={t('monthlyReport.expenses.none')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="pb-2 pr-3 font-medium">{t('monthlyReport.expenses.category')}</th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.expenses.amount')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.expenses.previous')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.expenses.average3m')}
                </th>
                <th className="pb-2 text-right font-medium">{t('monthlyReport.expenses.share')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {expenses.categories.map((c) => {
                const spike = c.average3m > 0 && c.amount > c.average3m * 1.3;
                return (
                  <tr key={c.code}>
                    <td className="py-1.5 pr-3 text-gray-800">{c.label}</td>
                    <td
                      className={`py-1.5 pr-3 text-right tabular-nums font-medium ${spike ? 'text-amber-700' : 'text-gray-900'}`}
                    >
                      {fmtMoney(c.amount, 'RON', locale)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500">
                      {fmtMoney(c.previous, 'RON', locale)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500">
                      {fmtMoney(c.average3m, 'RON', locale)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-gray-500">
                      {fmtPct(c.share)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
        <PartyList title={t('monthlyReport.expenses.topSuppliers')} rows={expenses.topSuppliers} />
        {expenses.subscriptions.length > 0 && (
          <div>
            <h3 className="flex items-center justify-between text-sm font-medium text-gray-700">
              {t('monthlyReport.expenses.subscriptions')}
              <span className="text-xs font-normal text-gray-500">
                {t('monthlyReport.expenses.subscriptionsTotal')}:{' '}
                {fmtMoney(expenses.subscriptionsTotal, 'RON', locale)}
              </span>
            </h3>
            <ul className="mt-2 divide-y divide-gray-100">
              {expenses.subscriptions.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                  <div className="min-w-0">
                    <div className="truncate text-gray-800">{s.name}</div>
                    {s.expectedForPeriod != null ? (
                      <div className="text-xs text-gray-400">
                        {t('monthlyReport.expenses.expectedForPeriod')}:{' '}
                        {fmtMoney(s.expectedForPeriod, s.expectedCurrency, locale)}
                      </div>
                    ) : s.expectedAmount !== null ? (
                      <div className="text-xs text-gray-400">
                        {t('monthlyReport.expenses.expected', {
                          amount: fmtMoney(s.expectedAmount, s.expectedCurrency, locale),
                        })}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="tabular-nums text-gray-900">
                      {s.payments > 0 ? fmtMoney(s.paidAmount, s.paidCurrency, locale) : '—'}
                    </span>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-xs font-medium ${SUBSCRIPTION_STATUS_CLASS[s.status]}`}
                    >
                      {t(`monthlyReport.expenses.status.${s.status}`)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            {expenses.subscriptionsRunRateMonthly ? (
              <p className="mt-2 text-xs text-gray-500">
                {t('monthlyReport.expenses.runRate')}:{' '}
                {t('monthlyReport.expenses.runRateValue', {
                  monthly: fmtMoney(expenses.subscriptionsRunRateMonthly, 'RON', locale),
                  annual: fmtMoney(expenses.subscriptionsRunRateAnnual ?? 0, 'RON', locale),
                })}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </ReportSection>
  );
}

// --- 5/6. Receivables + payables -----------------------------------------------------

export function OpenInvoicesSection({
  kind,
  data,
}: {
  kind: 'receivables' | 'payables';
  data: OpenInvoicesData | PayablesData;
}) {
  const { t, locale } = useI18n();
  const [showAll, setShowAll] = useState(false);
  const scheduled = kind === 'payables' ? (data as PayablesData).scheduledNextMonth : null;
  const items = showAll ? data.items : data.items.slice(0, OPEN_PREVIEW);
  const buckets = AGING_BUCKET_ORDER.map((key) => data.buckets.find((b) => b.key === key)).filter(
    (b): b is NonNullable<typeof b> => b !== undefined,
  );

  return (
    <ReportSection
      title={t(`monthlyReport.section.${kind}`)}
      aside={
        <span className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{fmtMoney(data.total, 'RON', locale)}</span>
          {' · '}
          {t('monthlyReport.open.count', { n: data.count })}
        </span>
      }
    >
      {data.items.length === 0 ? (
        <Empty
          text={t(`monthlyReport.open.none${kind === 'receivables' ? 'Receivables' : 'Payables'}`)}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {buckets.map((b) => {
              const tone =
                b.key === 'current'
                  ? 'text-gray-900'
                  : b.key === 'd1_30'
                    ? 'text-amber-700'
                    : 'text-red-700';
              return (
                <div key={b.key} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">{t(`monthlyReport.open.bucket.${b.key}`)}</p>
                  <p className={`mt-1 text-base font-semibold tabular-nums ${tone}`}>
                    {fmtMoney(b.amount, 'RON', locale)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t('monthlyReport.open.count', { n: b.count })}
                  </p>
                </div>
              );
            })}
          </div>
          {data.topParty && (
            <p className="mt-3 text-sm text-gray-700">
              {t('monthlyReport.open.topParty')}:{' '}
              <span className="font-medium text-gray-900">{data.topParty.name}</span>{' '}
              <span className="tabular-nums">{fmtMoney(data.topParty.amount, 'RON', locale)}</span>
            </p>
          )}
          {data.otherCurrencies.length > 0 && (
            <p className="mt-1 text-xs text-gray-500">
              {t('monthlyReport.open.otherCurrencies')}:{' '}
              {data.otherCurrencies.map((c) => fmtMoney(c.amount, c.currency, locale)).join(' · ')}
            </p>
          )}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="pb-2 pr-3 font-medium">{t('monthlyReport.open.invoice')}</th>
                  <th className="pb-2 pr-3 font-medium">{t('monthlyReport.open.party')}</th>
                  <th className="pb-2 pr-3 text-right font-medium">
                    {t('monthlyReport.open.amount')}
                  </th>
                  <th className="pb-2 text-right font-medium">{t('monthlyReport.open.dueDate')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((i) => (
                  <tr key={`${i.source}:${i.id}`}>
                    <td className="py-1.5 pr-3 font-medium text-gray-900">{i.invoiceNumber}</td>
                    <td className="max-w-[16rem] truncate py-1.5 pr-3 text-gray-700">
                      {i.party ?? '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">
                      {fmtMoney(i.amount, i.currency, locale)}
                    </td>
                    <td className="py-1.5 text-right text-gray-600">
                      {fmtDate(i.dueDate, locale)}
                      {i.daysOverdue > 0 && (
                        <span className="ml-2 text-xs font-medium text-red-600">
                          {t('monthlyReport.open.daysOverdue', { n: i.daysOverdue })}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.items.length > OPEN_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 text-sm font-medium text-brand-700 hover:underline"
            >
              {showAll
                ? t('monthlyReport.open.showLess')
                : t('monthlyReport.open.showAll', { n: data.items.length })}
            </button>
          )}
        </>
      )}
      {scheduled && (scheduled.payments.count > 0 || scheduled.receipts.count > 0) && (
        <div className="mt-4 border-t border-gray-100 pt-3 text-sm text-gray-700">
          <span className="font-medium text-gray-900">
            {t('monthlyReport.open.scheduledNext')}:
          </span>{' '}
          {t('monthlyReport.open.scheduledPayments')}{' '}
          <span className="tabular-nums">{fmtMoney(scheduled.payments.total, 'RON', locale)}</span>{' '}
          ({scheduled.payments.count}) · {t('monthlyReport.open.scheduledReceipts')}{' '}
          <span className="tabular-nums">{fmtMoney(scheduled.receipts.total, 'RON', locale)}</span>{' '}
          ({scheduled.receipts.count})
        </div>
      )}
    </ReportSection>
  );
}

// --- 7. VAT --------------------------------------------------------------------------

export function VatSection({ vat }: { vat: VatData }) {
  const { t, locale } = useI18n();
  return (
    <ReportSection title={t('monthlyReport.section.vat')}>
      {!vat.applicable ? (
        <Empty text={t('monthlyReport.vat.notApplicable')} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {(
              [
                [t('monthlyReport.vat.periodLabel'), vat.period ?? vat.month],
                [t('monthlyReport.vat.quarter'), vat.quarter],
              ] as const
            ).map(([heading, p]) => {
              return (
                <div key={p.label} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {heading} · {p.label}
                  </p>
                  <dl className="mt-2 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-600">{t('monthlyReport.vat.collected')}</dt>
                      <dd className="tabular-nums text-gray-900">
                        {fmtMoney(p.collected, 'RON', locale)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600">{t('monthlyReport.vat.deductible')}</dt>
                      <dd className="tabular-nums text-gray-900">
                        {fmtMoney(p.deductible, 'RON', locale)}
                      </dd>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold">
                      <dt className="text-gray-900">
                        {p.balance >= 0
                          ? t('monthlyReport.vat.toPay')
                          : t('monthlyReport.vat.toRecover')}
                      </dt>
                      <dd
                        className={`tabular-nums ${p.balance >= 0 ? 'text-red-700' : 'text-green-700'}`}
                      >
                        {fmtMoney(Math.abs(p.balance), 'RON', locale)}
                      </dd>
                    </div>
                  </dl>
                </div>
              );
            })}
          </div>
          {vat.quarters && vat.quarters.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700">
                {t('monthlyReport.vat.quarters')}
              </h3>
              <ul className="mt-2 divide-y divide-gray-100">
                {vat.quarters.map((q) => (
                  <li key={q.label} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-gray-800">{q.label}</span>
                    <span className="shrink-0 tabular-nums text-gray-600">
                      {fmtMoney(q.collected, 'RON', locale)} −{' '}
                      {fmtMoney(q.deductible, 'RON', locale)} ={' '}
                      <span
                        className={`font-medium ${q.balance >= 0 ? 'text-red-700' : 'text-green-700'}`}
                      >
                        {fmtMoney(Math.abs(q.balance), 'RON', locale)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500">{t('monthlyReport.vat.note')}</p>
        </>
      )}
    </ReportSection>
  );
}

// --- 8. Data quality -----------------------------------------------------------------

export function DataQualitySection({ quality }: { quality: DataQualityData }) {
  const { t, locale } = useI18n();
  const tx = quality.transactions;
  return (
    <ReportSection title={t('monthlyReport.section.dataQuality')}>
      {quality.warnings.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4" />
          {t('monthlyReport.quality.allGood')}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {quality.warnings.map((w) => (
            <li key={w} className="flex items-start gap-2 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span>{t(`monthlyReport.quality.warning.${w}`)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        <div>
          <p className="text-xs text-gray-500">{t('monthlyReport.quality.transactions')}</p>
          <p className="font-semibold text-gray-900">{tx.total}</p>
          <p className="text-xs text-gray-500">
            {t('monthlyReport.quality.reconciled')} {tx.reconciled} ·{' '}
            <span className={tx.unmatched > 0 ? 'text-amber-700' : ''}>
              {t('monthlyReport.quality.unmatched')} {tx.unmatched}
              {tx.unmatched > 0 && ` (${fmtMoney(tx.unmatchedAmount, 'RON', locale)})`}
            </span>{' '}
            · {t('monthlyReport.quality.ignored')} {tx.ignored}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('monthlyReport.quality.statements')}</p>
          <p className="font-semibold text-gray-900">
            {quality.statements.total}{' '}
            <span className="text-xs font-normal text-gray-500">
              ({quality.statements.finalized} {t('monthlyReport.quality.finalized')})
            </span>
          </p>
          <p className="text-xs text-gray-500">
            {t('monthlyReport.quality.accountsWithoutStatement')}:{' '}
            {quality.accountsWithoutStatement}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('monthlyReport.quality.receivedUnpaid')}</p>
          <p className="font-semibold text-gray-900">
            {quality.receivedInvoicesUnpaid.count}{' '}
            <span className="text-xs font-normal text-gray-500">
              {fmtMoney(quality.receivedInvoicesUnpaid.total, 'RON', locale)}
            </span>
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('monthlyReport.quality.ledgerRows')}</p>
          <p className="font-semibold text-gray-900">{quality.ledgerRows}</p>
        </div>
      </div>
      {quality.warnings.length > 0 && (
        <Link
          to="/financials/bank-statements"
          className="mt-3 inline-block text-sm font-medium text-brand-700 hover:underline"
        >
          {t('monthlyReport.quality.goToStatements')} →
        </Link>
      )}
    </ReportSection>
  );
}

// --- 9. Projects ---------------------------------------------------------------------

export function ProjectsSection({ projects }: { projects: ProjectsData }) {
  const { t, locale } = useI18n();
  return (
    <ReportSection title={t('monthlyReport.section.projects')}>
      {projects.rows.length === 0 ? (
        <Empty text={t('monthlyReport.projects.none')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="pb-2 pr-3 font-medium">{t('monthlyReport.projects.project')}</th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.projects.revenue')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.projects.expenses')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.projects.margin')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.projects.hours')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.projects.laborCost')}
                </th>
                <th className="pb-2 pr-3 text-right font-medium">
                  {t('monthlyReport.projects.marginWithLabor')}
                </th>
                <th className="pb-2 text-right font-medium">
                  {t('monthlyReport.projects.budget')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {projects.rows.map((p) => (
                <tr key={p.projectId}>
                  <td className="py-1.5 pr-3 text-gray-800">
                    {p.code && <span className="mr-1 text-xs text-gray-400">{p.code}</span>}
                    {p.name}
                    {p.isBarter && (
                      <span className="ml-1.5 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700">
                        {t('monthlyReport.projects.barter')}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">
                    {fmtMoney(p.revenue, 'RON', locale)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">
                    {fmtMoney(p.expenses, 'RON', locale)}
                  </td>
                  <td
                    className={`py-1.5 pr-3 text-right tabular-nums font-medium ${p.margin >= 0 ? 'text-green-700' : 'text-red-700'}`}
                  >
                    {fmtSignedMoney(p.margin, 'RON', locale)}
                    {p.marginPct !== null && (
                      <span className="ml-1 text-xs font-normal text-gray-400">
                        {fmtPct(p.marginPct)}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                    {p.hours ? p.hours.toLocaleString(locale === 'ro' ? 'ro-RO' : 'en-GB') : '—'}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                    {p.laborCost ? fmtMoney(p.laborCost, 'RON', locale) : '—'}
                  </td>
                  <td
                    className={`py-1.5 pr-3 text-right tabular-nums ${
                      p.marginWithLabor === undefined
                        ? 'text-gray-400'
                        : p.marginWithLabor >= 0
                          ? 'text-green-700'
                          : 'text-red-700'
                    }`}
                  >
                    {p.marginWithLabor === undefined
                      ? '—'
                      : fmtSignedMoney(p.marginWithLabor, 'RON', locale)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-600">
                    {p.budget == null ? (
                      '—'
                    ) : (
                      <>
                        {fmtMoney(p.budget, p.budgetCurrency ?? 'RON', locale)}
                        {p.budgetUsedPct != null && (
                          <span
                            className={`ml-1 text-xs font-normal ${p.budgetUsedPct > 100 ? 'text-red-600' : 'text-gray-400'}`}
                          >
                            {fmtPct(p.budgetUsedPct)}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="text-gray-500">
                <td className="py-1.5 pr-3">{t('monthlyReport.projects.unallocated')}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {fmtMoney(projects.unallocated.revenue, 'RON', locale)}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {fmtMoney(projects.unallocated.expenses, 'RON', locale)}
                </td>
                <td colSpan={4} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {projects.unallocatedExpensesShare !== null && projects.unallocatedExpensesShare > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {t('monthlyReport.projects.unallocatedShare', {
            share: projects.unallocatedExpensesShare,
          })}
        </p>
      )}
      {projects.losingProjects ? (
        <p className="mt-2 text-xs font-medium text-red-600">
          {t('monthlyReport.projects.losing', { n: projects.losingProjects })}
        </p>
      ) : null}
      {(projects.warnings ?? []).map((w) => (
        <p key={w} className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          <span>
            {w === 'hours_without_rate'
              ? t('monthlyReport.projects.warning.hours_without_rate', {
                  n: projects.hoursWithoutRateTotal ?? 0,
                })
              : t(
                  `monthlyReport.projects.warning.${w}` as 'monthlyReport.projects.warning.labor_estimated',
                )}
          </span>
        </p>
      ))}
    </ReportSection>
  );
}
